import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { submitQuote, quoteSchema, type QuoteInput } from "@/lib/quote.functions";
import { toast } from "sonner";

type Props = {
  sourcePage: string;
  defaultService?: string;
  compact?: boolean;
};

export function QuoteForm({ sourcePage, defaultService, compact }: Props) {
  const submit = useServerFn(submitQuote);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const input: QuoteInput = {
      name: String(fd.get("name") || ""),
      email: String(fd.get("email") || ""),
      phone: String(fd.get("phone") || ""),
      address: String(fd.get("address") || ""),
      service_type: String(fd.get("service_type") || defaultService || ""),
      message: String(fd.get("message") || ""),
      source_page: sourcePage,
    };
    const parsed = quoteSchema.safeParse(input);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please review the form.");
      return;
    }
    setPending(true);
    try {
      await submit({ data: parsed.data });
      setDone(true);
      toast.success("Request received — we'll be in touch shortly.");
      (e.target as HTMLFormElement).reset();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setPending(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center">
        <div className="font-serif text-xl text-primary">Thank you</div>
        <p className="mt-2 text-sm text-muted-foreground">
          We've received your request and will reach out within one business day.
        </p>
        <button
          type="button"
          onClick={() => setDone(false)}
          className="mt-4 text-sm text-primary underline"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={`rounded-xl border border-border bg-card p-5 sm:p-6 ${compact ? "" : "shadow-sm"}`}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field name="name" label="Full name" required />
        <Field name="email" label="Email" type="email" required />
        <Field name="phone" label="Phone" type="tel" />
        <Field name="address" label="Property address" />
        <div className="sm:col-span-2">
          <label htmlFor="quote-service-type" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Service</label>
          <select
            id="quote-service-type"
            name="service_type"
            defaultValue={defaultService ?? "Home Watch"}
            className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
          >
            <option>Home Watch</option>
            <option>Junk Removal</option>
            <option>Estate Cleanout</option>
            <option>Property Cleanout</option>
            <option>Garage Cleanout</option>
            <option>Foreclosure Cleanout</option>
            <option>Eviction Cleanout</option>
            <option>Storm Debris Removal</option>
            <option>Furniture Removal</option>
            <option>Appliance Removal</option>
            <option>Concierge Services</option>
            <option>Other</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="quote-message" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">How can we help?</label>
          <textarea
            id="quote-message"
            name="message"
            rows={4}
            className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
            placeholder="Tell us about your property and what you need."
          />
        </div>
      </div>
      <button
        type="submit"
        disabled={pending}
        className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60 sm:w-auto"
      >
        {pending ? "Sending…" : "Request My Free Quote"}
      </button>
      <p className="mt-3 text-xs text-muted-foreground">
        We respond within one business day. No spam, ever.
      </p>
    </form>
  );
}

function Field({ name, label, type = "text", required }: { name: string; label: string; type?: string; required?: boolean }) {
  const id = `quote-${name}`;
  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-destructive"> *</span>}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        required={required}
        className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
      />
    </div>
  );
}